import { Icon } from '@iconify/react'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useLoaderData, useRouter } from '@tanstack/react-router'
import { Fragment, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { fetchRolesList, updateRolePrivileges } from '~features/roles/api'
import { Roles } from '~features/roles/types'
import { Button, Card, CardContent, IconButton } from '~in-app/components'
import MultiSelectDropdown, { Option } from '~in-app/components/MultiSelectDropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchPriviledgesList } from '~in-app/shareable/priviledges/api'
import { ListingResponse } from '~in-app/types/api.dto'

export const Route = createFileRoute('/console/roles/assign')({
  component: AssignRolePage,
  loader: async ({ abortController }) => {
    let roleList: {
      label: string
      value: string
      priviledges: any[]
    }[] = []
    let priviledges: {
      roles: any
      id: string
      name: string
      children?: any[]
    }[] = []

    const privilegeValue = auth$.privileges.get()
    const distributorResponse = privilegeValue?.has('0.3.5.0')
      ? await fetchRolesList({
        abortCtrl: abortController,
      })
      : {} as ListingResponse<Roles>
    roleList = distributorResponse
      ? distributorResponse?.list?.map((item: any) => ({
        value: item.id,
        label: item.name,
        priviledges: item.priviledges,
      }))
      : []

    const priviledgeResponse = await fetchPriviledgesList({
      abortCtrl: abortController,
    })
    const map: { [key: string]: any } = {}
    priviledgeResponse?.list.forEach((item: { id: string, name: string, parent?: { id: string } }) => {
      map[item.id] = { ...item, name: `(${item.id}) - ${item.name}`, children: [] }
    })

    // Step 2: Build the hierarchical structure
    priviledgeResponse?.list.forEach((item: { id: string, parent?: { id: string } }) => {
      if (item.parent && item.parent.id) {
      // Add as child to its parent
        map[item.parent.id]?.children.push(map[item.id])
      }
      else {
      // No parent, add to the root level
        priviledges.push(map[item.id])
      }
    })

    return { roleList, priviledges, privilegeValue }
  },

  staticData: { privileges: ['0.3.5.5'] },
})

function AssignRolePage() {
  const router = useRouter()
  const { roleList, priviledges, privilegeValue } = useLoaderData({ from: '/console/roles/assign' })
  type PrivilegeMap = {
    [key: string]: string[] // The key is a string, and the value is an array of strings.
  }
  type PermissionNode = {
    roles: { [key: string]: boolean } // Role ID mapped to a boolean
    id: string // Permission ID
    children: PermissionNode[] // Children permissions
  }
  const [permissions, setPermissions] = useState(priviledges)
  const [collapsedIds, setCollapsedIds] = useState<string[]>([])
  const [roleIds, setRoleIds] = useState<Option[]>([])

  useEffect(() => {
    if (roleIds.length > 0) {
      // Extract the role ID values
      const roleIdSet = new Set(roleIds.map(role => role.value))
      // Filter the privileges from the roleList that match the role IDs
      const matchedPrivileges = roleList.filter((role) => {
        return roleIdSet.has(role.value)
      })

      function populateRoles(permissionList: any[], matchedPrivileges: any[]): any[] {
        // Helper function to recursively populate roles
        return permissionList.map((permission) => {
          // Create a new object for the updated permission
          const updatedPermission = {
            ...permission,
            roles: {
              ...permission.roles,
            },
          }

          // Check if the permission matches any privilege from matchedPrivileges
          matchedPrivileges.forEach((matchedPrivilege) => {
            // Ensure `priviledges` exists and is an array before iterating
            if (Array.isArray(matchedPrivilege.priviledges)) {
              matchedPrivilege.priviledges.forEach((privilege: { id: any }) => {
                if (permission.id === privilege.id) {
                  // If there is a match, add the role value to the roles object
                  updatedPermission.roles[matchedPrivilege.value] = true
                }
              })
            }
          })

          // Remove roles that do not have a matching role ID in roleIds
          Object.keys(updatedPermission.roles).forEach((roleId) => {
            if (!roleIdSet.has(roleId)) {
              delete updatedPermission.roles[roleId]
            }
          })

          // Recursively update children if they exist
          if (permission.children && permission.children.length > 0) {
            updatedPermission.children = populateRoles(permission.children, matchedPrivileges)
          }

          return updatedPermission
        })
      }

      try {
        const updatedPermissions = populateRoles(permissions, matchedPrivileges)
        setPermissions(updatedPermissions)
      }
      catch (error) {
        console.error('Error updating permissions:', error)
      }
    }
  }, [roleIds, roleList])

  const handleToggleCollapse = (id: string) => {
    setCollapsedIds(prev =>
      prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id],
    )
  }
  // Helper function to find a node by ID (returns a reference)
  const findNodeById = (nodes: any[], id: string): any | null => {
    for (let node of nodes) {
      if (node.id === id) return node
      if (node.children) {
        let found = findNodeById(node.children, id)
        if (found) return found
      }
    }
    return null
  }
  const updateChildRoles = (node: any, roleId: string | number, isChecked: boolean) => {
    if (node.children) {
      node.children.forEach((child: any) => {
        // Update the child's roles property
        child.roles = {
          ...child.roles,
          [roleId]: isChecked,
        }
        // Recursively update all descendants
        updateChildRoles(child, roleId, isChecked)
      })
    }
  }
  // Function to update roles recursively for parents
  const updateParentRoles = (updatedPermissions: any[], node: any, roleId: string | number, isChecked: boolean) => {
    if (!node || !node.parent) return

    const parentNode = findNodeById(updatedPermissions, node.parent.id)

    if (parentNode) {
      // Check if any child of the parent has roles[roleId] set to true
      const hasChildWithRoleTrue = parentNode.children?.some((child: any) => {
        return child.roles && child.roles[roleId] === true
      })

      // Update the parent's roles property
      parentNode.roles = {
        ...parentNode.roles,
        [roleId]: hasChildWithRoleTrue ? true : isChecked,
      }

      // Recursively update the next parent up the hierarchy
      updateParentRoles(updatedPermissions, parentNode, roleId, parentNode.roles[roleId])
    }
  }

  // Function to update a specific node's roles and trigger parent updates
  const updateNodeRoles = (nodeId: string, roleId: string, isChecked: boolean) => {
    // Create a deep copy of permissions to update safely
    const updatedPermissions = JSON.parse(JSON.stringify(permissions))
    const node = findNodeById(updatedPermissions, nodeId)

    if (node) {
      // Update the node's roles
      node.roles = {
        ...node.roles,
        [roleId]: isChecked,
      }
      // Update all child nodes recursively
      updateChildRoles(node, roleId, isChecked)

      // Update parents after updating the node
      updateParentRoles(updatedPermissions, node, roleId, isChecked)

      // Update the state with the modified permissions
      setPermissions(updatedPermissions)
    }
  }

  // Handle toggle permission event
  const handleTogglePermission = (selectedPrivilege: any, roleId: string, isChecked: boolean) => {
    updateNodeRoles(selectedPrivilege.id, roleId, isChecked)
  }

  const renderPrivileges = (items: any[], level: number = 0) => (
    items.map(privilege => (
      <Fragment key={privilege?.id}>
        <tr className="items-center">
          <td
            className="border-b border-r-4 border-slate-200 bg-white py-2 dark:border-gray-700 dark:bg-gray-800"
            style={
              { paddingLeft: `${(level + 1) * 20}px`, display: 'flex', alignItems: 'center', position: 'sticky', minWidth: '400px',
                left: 0 }
            }
            onClick={() => handleToggleCollapse(privilege?.id)}

          >
            {
              privilege && privilege?.children?.length > 0 && (
                <IconButton
                  className="size-4 bg-transparent"
                  color="muted"
                  style={{ marginRight: '8px' }}
                >
                  <Icon icon={collapsedIds.includes(privilege?.id) ? 'lucide:plus' : 'lucide:minus'} />
                </IconButton>
              )
            }

            {privilege?.name}
          </td>

          {
            roleIds.map(role => (
              <td
                key={role.value}
                className="items-center border-b bg-white py-2 text-center dark:border-gray-700 dark:bg-gray-800"
              >
                <input
                  checked={privilege?.roles?.[role.value]}
                  type="checkbox"
                  onChange={e => handleTogglePermission(privilege, role?.value, e.target.checked)}
                />
              </td>
            ))
          }
        </tr>

        {
          privilege?.children && !collapsedIds.includes(privilege?.id)
          && renderPrivileges(privilege?.children, level + 1)
        }
      </Fragment>
    ))
  )

  const updateRolePriviledgesFunc = () => {
    const mutation = useMutation({
      mutationFn: ({ id, priviledges }: { id: string, priviledges: string[] }) => updateRolePrivileges(id, priviledges),
      onSuccess: () => {
        toast.success('Roles status updated successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
        toast.error(`Error updating Roles status: ${error.message}`, { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
    })

    return mutation
  }
  const updateRolePriviledgesMutation = updateRolePriviledgesFunc()
  const assignRoles = async () => {
    try {
      const submitValue = JSON.parse(JSON.stringify(permissions)) as PermissionNode[] // Ensure correct type
      function flattenRoles(data: PermissionNode[]): PrivilegeMap {
        const roleMap: PrivilegeMap = {}

        function traverse(node: PermissionNode) {
          // Check if node and its roles are defined
          if (node && node.roles) {
            // Iterate over the roles in the current node
            Object.keys(node.roles).forEach((roleId) => {
              // Only add the role if it's true
              if (node.roles[roleId]) {
                // Initialize the role array if it doesn't exist
                if (!roleMap[roleId]) {
                  roleMap[roleId] = []
                }
                // Add the current node's ID to the role's array
                roleMap[roleId].push(node.id)
              }
            })
          }

          // Recursively traverse children if they exist
          if (node.children) {
            node.children.forEach((child: PermissionNode) => traverse(child))
          }
        }

        // Start traversing from the root nodes
        data.forEach(rootNode => traverse(rootNode))
        return roleMap
      }

      const result = flattenRoles(submitValue)
      const hasEmptyRole = Object.keys(result).length === 0
      if (hasEmptyRole) {
        toast.error(`Some roles have no privileges assigned`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })
      }
      else {
        Object.keys(result).forEach((roleId) => {
          const privilegeIds = result[roleId]
          updateRolePriviledgesMutation.mutate({ id: roleId, priviledges: privilegeIds })
        })
      }
    }
    catch (error) {
      console.error(error)
      toast.error(`Submit Error: ${error}`, {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 500,
      })
    }
  }

  return (
    <div>
      <div className="p-2">
        <IconButton
          color="muted"
          onClick={() => router.history.back()}
        >
          <Icon icon="lucide:arrow-left" />
        </IconButton>

        <span className="mb-4 ml-4 text-2xl font-bold">Assign Roles Page</span>
      </div>

      <div className="flex items-center justify-between p-2">
        <div className="grid w-full grid-cols-2 gap-2">
          <MultiSelectDropdown
            value={roleIds}
            options={
              roleList.map(item => ({
                label: item.label,
                value: item.value,
              }))
            }
            onChange={
              (e) => {
                setRoleIds(e)
              }
            }
          />
        </div>

        <div>
          <Button
            className="border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
            color="muted"
            disabled={!privilegeValue?.has('0.3.5.5') || roleIds.length === 0}
            onClick={() => assignRoles()}
          >
            Save
          </Button>
        </div>

      </div>

      <Card className="mt-4">
        <CardContent className="mt-4">
          <div
            className="p-2"
            style={
              {
                overflowX: 'auto',
                width: '100%',
                overflowY: 'auto',
                maxHeight: '65vh',
              }
            }
          >
            {
              roleIds.length === 0
                ? (
                    <p className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400">
                      Please select roles.
                    </p>
                  )
                : (
                    <table
                      className="border border-inherit text-left text-sm text-gray-500 dark:text-gray-400 sm:rounded-lg"
                      style={
                        {
                          width: '100%',
                          tableLayout: 'fixed', // Maintain fixed layout
                        }
                      }
                    >
                      <thead>
                        <tr>
                          <th
                            className="border-r-4 border-slate-200 bg-gray-100 py-3 text-center text-base uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400"
                            style={
                              {
                                width: '400px', // Use width instead of minWidth
                                position: 'sticky',
                                left: 0,
                                top: 0,
                              }
                            }
                          >
                            Privilege
                          </th>

                          {
                            roleIds.map(role => (
                              <th
                                key={role.value}
                                className="break-all border-x border-slate-100 bg-gray-100 py-3 text-center text-sm uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400"
                                style={{ width: '150px' }} // Ensure a fixed width for consistency
                              >
                                {role.label}
                              </th>
                            ))
                          }
                        </tr>
                      </thead>

                      <tbody>
                        {
                          permissions.length === 0
                            ? (
                                <tr>
                                  <td
                                    className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400"
                                    colSpan={roleIds.length + 1}
                                  >
                                    No results found.
                                  </td>
                                </tr>
                              )
                            : renderPrivileges(permissions)
                        }
                      </tbody>
                    </table>
                  )
            }
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
